/**
 * Supply (BLU-1268) as a mark: one glyph, one cell look and one badge per
 * mode, shared by every surface that shows how a blank is supplied at a
 * location (the catalog picker's grid, the Base garments Supply column and
 * Supply view, the Inventory list, matrix and product detail).
 *
 *   - Just in time: a clock. Dashed brand tint in a grid cell (nothing is held),
 *     the brand tint badge elsewhere: it is the mode that changes how stock
 *     behaves, so it carries the one accent.
 *   - Stocked: a box. The default, so its badge is quiet words.
 *   - Managed elsewhere: a building (someone else runs this stock). Slate.
 *
 * Colour is never the only signal (DESIGN.md section 16): every look has its
 * own glyph and the badge always carries the words.
 */
import type { ReactNode } from 'react';
import { Building2, Clock, Package, type LucideIcon } from 'lucide-react';
import type { StockMode } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { useSupplyWords } from '../hooks/useSupplyWords';
import { SUPPLY_CELL_STATIC } from '../lib/supply-looks';

const GLYPH: Record<StockMode, LucideIcon> = {
  just_in_time: Clock,
  stocked: Package,
  managed_elsewhere: Building2,
};

export function SupplyGlyph({ mode, className }: { mode: StockMode; className?: string }) {
  const Icon = GLYPH[mode];
  return <Icon className={cn('h-3.5 w-3.5 shrink-0', className)} aria-hidden="true" />;
}

const BADGE_LOOK: Record<StockMode, string> = {
  just_in_time: 'bg-primary/10 text-primary-strong ring-1 ring-threadheads-purple/30',
  stocked: 'text-muted-foreground',
  managed_elsewhere: 'bg-slate-50 text-slate-700 ring-1 ring-slate-300',
};

export interface SupplyBadgeProps {
  mode: StockMode;
  className?: string;
  /** Read before the words by screen readers only ("Supply at Ningbo: Just in time"). */
  srPrefix?: string;
}

/** The mode in words with its glyph, sized for a table cell. */
export function SupplyBadge({ mode, className, srPrefix }: SupplyBadgeProps) {
  const words = useSupplyWords();
  return (
    <span
      data-supply={mode}
      className={cn(
        'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-md text-[11px] font-medium leading-4',
        mode === 'stocked' ? 'py-0.5' : 'px-1.5 py-0.5',
        BADGE_LOOK[mode],
        className,
      )}
    >
      <SupplyGlyph mode={mode} className="h-3 w-3" />
      {srPrefix && <span className="sr-only">{srPrefix}: </span>}
      {words[mode]}
    </span>
  );
}

/** A small swatch of one mode's cell look, for a legend. */
export function SupplySwatch({ mode }: { mode: StockMode }) {
  return (
    <span className={cn('flex h-5 w-6 shrink-0 items-center justify-center rounded border-2', SUPPLY_CELL_STATIC[mode])}>
      <SupplyGlyph mode={mode} className="h-3 w-3" />
    </span>
  );
}

/** A Supply grid's key: each mode's look in words, then what a press does (`children`). */
export function SupplyLegend({ modes, children }: { modes: readonly StockMode[]; children?: ReactNode }) {
  const words = useSupplyWords();
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
      {modes.map((mode) => (
        <span key={mode} className="inline-flex items-center gap-1.5">
          <SupplySwatch mode={mode} />
          {words[mode]}
        </span>
      ))}
      {children}
    </div>
  );
}
